const TYPE_DEFAULTS = {
  'epx:StartEvent': { name: 'Start' },
  'epx:EndEvent': { name: 'End' },
  'epx:UserTask': { name: 'User Task' },
  'epx:ServiceTask': { name: 'Service Task' },
  'epx:ScriptTask': { name: 'Script Task' },
  'epx:ManualTask': { name: 'Manual Task' },
  'epx:SendTask': { name: 'Send Task' },
  'epx:ReceiveTask': { name: 'Receive Task' },
  'epx:BusinessRuleTask': { name: 'Business Rule' },
  'epx:CallActivity': { name: 'Call Activity' },
  'epx:ExclusiveGateway': { name: 'Gateway' },
  'epx:ParallelGateway': { name: 'Parallel' },
  'epx:InclusiveGateway': { name: 'Inclusive' },
  'epx:ComplexGateway': { name: 'Complex' },
  'epx:EventBasedGateway': { name: 'Event GW' },
  'epx:IntermediateEvent': { name: 'Event' },
  'epx:IntermediateThrowEvent': { name: 'Throw Event' },
  'epx:BoundaryEvent': { name: 'Boundary' },
  'epx:SubProcess': { name: 'Sub-Process' },
  'epx:Transaction': { name: 'Transaction' },
  'epx:Participant': { name: 'Pool' },
  'epx:Lane': { name: 'Lane' },
  'epx:DataObjectReference': { name: 'Data Object' },
  'epx:DataStoreReference': { name: 'Data Store' },
  'epx:Group': { name: '' },
  'epx:TextAnnotation': { name: '' },
};

export class DefaultPropsInterceptor {
  constructor(lf) {
    this._lf = lf;
    // Custom defaults registered via addDefaultProps()
    this._customDefaults = {};

    lf.on('node:add', ({ data }) => {
      const builtinDefaults = TYPE_DEFAULTS[data.type] || {};
      const customDefaults = this._customDefaults[data.type] || {};
      const incoming = data.properties || {};

      // AI presets: when the palette/context-pad drops a ServiceTask with
      // `aiKind` or a SubProcess with `isAiAgent`, layer in IA-specific
      // defaults (provider/model/timeout/etc.) before the user's overrides.
      let aiDefaults = {};
      if (data.type === 'epx:ServiceTask' && incoming.aiKind) {
        aiDefaults = {
          name: 'AI Service',
          provider: 'openai',
          model: 'gpt-4o-mini',
          timeoutMs: 30000,
        };
      } else if (data.type === 'epx:SubProcess' && incoming.isAiAgent) {
        aiDefaults = {
          name: 'AI Agent',
          provider: 'openai',
          model: 'gpt-4o-mini',
          maxIterations: 6,
          memoryScope: 'session',
        };
      }

      const merged = Object.assign(
        {},
        builtinDefaults,
        customDefaults,
        aiDefaults,
        incoming
      );
      lf.setProperties(data.id, merged);

      // Set visible text label if the node was added without one (e.g. palette drop, context pad).
      // Skip shapes that render their own SVG text from properties.name.
      const CUSTOM_TEXT_TYPES = new Set([
        'epx:Participant', 'epx:Lane',
        'epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask', 'epx:ManualTask',
        'epx:SendTask', 'epx:ReceiveTask', 'epx:BusinessRuleTask', 'epx:CallActivity',
        'epx:SubProcess', 'epx:Transaction',
      ]);
      if (!data.text && merged.name && !CUSTOM_TEXT_TYPES.has(data.type)) {
        const model = lf.graphModel.getElement(data.id);
        if (model) {
          // For gateways and events, position text below the shape (like bpmn-js)
          let textY = model.y;
          const GATEWAY_TYPES = new Set([
            'epx:ExclusiveGateway', 'epx:ParallelGateway', 'epx:InclusiveGateway',
            'epx:ComplexGateway', 'epx:EventBasedGateway',
          ]);
          const EVENT_TYPES = new Set([
            'epx:StartEvent', 'epx:EndEvent', 'epx:IntermediateEvent',
            'epx:IntermediateThrowEvent', 'epx:BoundaryEvent',
          ]);
          if (GATEWAY_TYPES.has(data.type)) {
            textY = model.y + (model.ry || 30) + 18;
          } else if (EVENT_TYPES.has(data.type)) {
            textY = model.y + (model.r || 18) + 14;
          }

          model.text = {
            value: merged.name,
            x: model.x,
            y: textY,
            draggable: true,
            editable: true,
          };
        }
      }
    });

    lf.on('edge:add', ({ data }) => {
      // Edge defaults — extensible via addDefaultProps('edge', {...})
      const edgeDefaults = this._customDefaults['edge'] || {};
      if (Object.keys(edgeDefaults).length) {
        lf.setProperties(data.id, edgeDefaults);
      }
    });
  }

  /**
   * Register additional default properties for a given element type.
   * These are merged with built-in defaults (custom defaults take precedence).
   * @param {string} elementType - e.g. 'epx:UserTask'
   * @param {object} defaults - e.g. { myProp: 'value' }
   */
  addDefaultProps(elementType, defaults) {
    this._customDefaults[elementType] = Object.assign(
      this._customDefaults[elementType] || {},
      defaults
    );
  }
}
